import React from 'react';
import { X, Mail, MessageSquare } from 'lucide-react';

interface CustomerDetailPanelProps {
    customer: any;
    onClose: () => void;
    isDark: boolean;
    onOpenFullView?: () => void;
}

/** Salesforce-style: rechtes Detail-Panel mit Profil und Kontaktdaten */
export const CustomerDetailPanel = ({ customer, onClose, isDark, onOpenFullView }: CustomerDetailPanelProps) => {
    if (!customer) return null;

    const vertraege = customer.verträge || customer.vertraege || [];
    const totalBeitrag = vertraege.reduce((sum: number, v: any) => {
        const b = v.beitrag ?? v.JAB ?? 0;
        const num = typeof b === 'number' ? b : parseFloat(String(b).replace(/[^\d,.-]/g, '').replace(',', '.'));
        return sum + (isNaN(num) ? 0 : num);
    }, 0);

    const getInitials = () => {
        const v = (customer.vorname || '').trim();
        const n = (customer.name || '').trim();
        if (v && n) return `${v[0]}${n[0]}`.toUpperCase();
        if (n) return n.slice(0, 2).toUpperCase();
        return '?';
    };

    return (
        <aside className={`w-full md:w-80 lg:w-96 shrink-0 border-l overflow-y-auto app-glass-panel border-white/30 dark:border-white/10`}>
            <div className="p-6 space-y-6">
                <div className="flex justify-between items-start">
                    <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Kundendetails</h3>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Profil */}
                <div className="flex flex-col items-center text-center">
                    <div className="w-24 h-24 rounded-full bg-[#2F6BFF] text-white flex items-center justify-center text-2xl font-bold shadow-lg mb-3">
                        {getInitials()}
                    </div>
                    <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {customer.vorname} {customer.name}
                    </h2>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {customer.zustMA || customer.adp || 'Kunde'}
                    </p>
                    <div className="flex gap-2 mt-3">
                        {onOpenFullView && (
                            <button onClick={onOpenFullView} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${isDark ? 'bg-[#2F6BFF] text-white hover:bg-[#2563eb]' : 'bg-[#2F6BFF] text-white hover:bg-[#2563eb]'}`}>Vollständige Ansicht</button>
                        )}
                        <button className={`p-2 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'}`} title="Nachricht"><MessageSquare size={16} /></button>
                        <button className={`p-2 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'}`} title="E-Mail"><Mail size={16} /></button>
                    </div>
                </div>

                {/* Detail-Felder */}
                <div className="space-y-4">
                    <DetailRow label="Vorname" value={customer.vorname} isDark={isDark} />
                    <DetailRow label="Nachname" value={customer.name} isDark={isDark} />
                    <DetailRow label="E-Mail" value={customer.mail} isDark={isDark} isLink />
                    <DetailRow label="Telefon" value={customer.telefon || customer.handy} isDark={isDark} isLink />
                    <DetailRow label="Adresse" value={[customer.strasse, customer.plz, customer.ort].filter(Boolean).join(', ')} isDark={isDark} />
                    <DetailRow label="Geburtsdatum" value={customer.gebDatum} isDark={isDark} />
                    <DetailRow label="ADP" value={customer.adp} isDark={isDark} />
                    <DetailRow label="Verträge" value={String(vertraege.length)} isDark={isDark} />
                    <DetailRow label="Gesamtbeitrag" value={totalBeitrag > 0 ? `${totalBeitrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '-'} isDark={isDark} />
                </div>

                <div className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                    <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Letzter Kontakt</p>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {customer.lastContact || '–'}
                    </p>
                </div>
            </div>
        </aside>
    );
};

const DetailRow = ({ label, value, isDark, isLink }: { label: string; value?: string; isDark: boolean; isLink?: boolean }) => {
    if (value == null || value === '') return null;
    return (
        <div className="flex items-start gap-2">
            <div>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{label}</p>
                {isLink && (value.includes('@') || /^[\d\s\-\+]+$/.test(value)) ? (
                    <a
                        href={value.includes('@') ? `mailto:${value}` : `tel:${value}`}
                        className={`text-sm font-medium hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
                    >
                        {value}
                    </a>
                ) : (
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{value}</p>
                )}
            </div>
        </div>
    );
};
